// client/src/routes/app/settings/route.tsx — the settings area (04 §8.18) · AUTH-004.
//
// §8.18: "a DETAIL-TABS-style layout route with a sub-navigation; children gate individually".
// The tabs are route-linked `<Link>`s (§5.10), so the active tab survives refresh and share;
// TanStack sets `aria-current="page"` on the active one. Two tabs exist at M0 — SCR-SET-001
// and SCR-SET-002 are AUTH-004's; the rest arrive with their items and add a `<Link>` here.

import { Link, Outlet, createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '@/components/layout/PageHeader';
import { staffRolesVisible } from '@/config/staff-roles';
import { AccountSummary } from '@/features/settings/AccountSummary';
import { useCan } from '@/session';

// Not copy — a class name; kept out of the JSX so the literal-string rule reads it as what it is.
const ACTIVE_TAB = { className: 'tab-active' };

export const Route = createFileRoute('/app/settings')({
  component: SettingsLayout,
});

function SettingsLayout() {
  const { t } = useTranslation();
  // §8.18: „children gate individually (SET-003/004/009 `owner` only)". `Org.Settings` is what
  // 03 §17.1 grants to that role and to no other, and what the eleven GDPR endpoints enforce —
  // so the tab is hidden from a manager here and the route renders SCR-SYS-403 for the URL they
  // typed anyway (§9.2.2). Both halves, as `nav-items.ts` and `route-guards.tsx` do it.
  const canManageSettings = useCan('Org.Settings');

  // SCR-SET-004 · AUTH-006. Its own permission and not `Org.Settings`: 03 §17.1 grants both to
  // `owner` alone today, but they are different questions and the five member endpoints
  // enforce this one.
  // The tab is also the accountant's and the manager's only door, so it goes while those two
  // roles are withdrawn from view (D-2820); the route redirects the typed URL.
  const canManageMembers = useCan('Org.ManageMembers') && staffRolesVisible();

  return (
    <>
      <PageHeader title={t('settings.title')} />
      {/* The account in facts, above the tabs that change it (D-3843). */}
      <AccountSummary />
      <nav className="tabs tabs-border mb-6" aria-label={t('settings.tabsLabel')}>
        <Link to="/app/settings/profile" className="tab" activeProps={ACTIVE_TAB}>
          {t('settings.tabs.profile')}
        </Link>
        <Link to="/app/settings/security" className="tab" activeProps={ACTIVE_TAB}>
          {t('settings.tabs.security')}
        </Link>
        {/* SCR-SET-005 · ALR-004. Visible to every member: the screen's per-user half is
            theirs, and its org half gates itself (04 §8.18). */}
        <Link to="/app/settings/notification-rules" className="tab" activeProps={ACTIVE_TAB}>
          {t('settings.tabs.notificationRules')}
        </Link>
        {/* SCR-SET-004 · AUTH-006 — the half of AUTH-005 that was specified for M1 and never
            built. §8.18: „children gate individually (SET-003/004/009 `owner` only)". */}
        {canManageMembers ? (
          <Link to="/app/settings/members" className="tab" activeProps={ACTIVE_TAB}>
            {t('settings.tabs.members')}
          </Link>
        ) : null}
        {/* SCR-SET-009 · GDPR-002. */}
        {canManageSettings ? (
          <Link to="/app/settings/gdpr" className="tab" activeProps={ACTIVE_TAB}>
            {t('settings.tabs.gdpr')}
          </Link>
        ) : null}
      </nav>
      <Outlet />
    </>
  );
}
